import { type ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAngularContext } from 'AngularContext';
import { useRootScope } from 'FrontRoyalAngular';
import {
    type CamelCasedEducationExperience,
    type CamelCasedTranscript,
    orgName,
    unofficialTranscripts,
} from 'CareerProfiles';
import { getDegreeName } from 'Careers';
import { navigationHelper } from 'NavigationHelpers';
import { Text } from 'Text';
import { InlineLink } from 'InlineLink';
import { MailtoLink } from 'MailtoLink';
import { getProgramConfigValue } from 'Program';
import transformKeyCase from 'Utils/transformKeyCase';
import { type CurrentUser, getAoiRecord } from 'Users';
import { DOCUMENT_UPLOAD_ACCEPT, useUploadDocument } from 'FrontRoyalUpload';
import { type PrivateUserDocumentsHelper, type TranscriptViewConfig } from '../editDocuments.types';
import { sortEducationExperiencesForDisplay, transcriptSectionUserProgramState } from '../transcriptViewConfig';
import { DocumentUploadButton } from './DocumentUploadButton';
import { UploadedDocumentList } from './UploadedDocumentList';

const UPLOAD_URL = `${window.ENDPOINT_ROOT}/api/s3_transcript_assets.json`;

const transcriptDownloadUrl = (id: string) => `${window.ENDPOINT_ROOT}/api/s3_transcript_assets/${id}/file`;

// The help-center article each linked sub-text key points at. These used to live inside the locale
// strings themselves as `ng-click="loadFaqUrl(...)"` attributes; in React the link target belongs in
// code and the string just marks where the link goes.
const FAQ_PATHS: Record<string, string> = {
    'settings.edit_documents.unofficial_transcripts_description_part_one': '/help/student-transcript-policy',
    'settings.edit_documents.unofficial_transcripts_please_upload':
        '/help/am-i-required-to-provide-official-documents-transcript-diploma-etc#unofficial',
    'settings.edit_documents.official_transcripts_required': '/help/transcript-faqs',
    'settings.edit_documents.unofficial_transcripts_final_note': '/help/transcript-faqs#what',
};

const FALLBACK_FAQ_PATH = '/help';

type FaqComponents = (path: string) => Record<string, ReactElement>;

// Renders the transcript section in whichever of the four shapes applies (see transcriptViewConfig).
export function TranscriptSection({
    config,
    currentUser,
    cannotUploadOnThisDevice,
}: {
    config: TranscriptViewConfig;
    currentUser: CurrentUser;
    cannotUploadOnThisDevice: boolean;
}) {
    const { t } = useTranslation('back_royal');
    const { loadFaqUrl } = navigationHelper();

    const experiences = sortEducationExperiencesForDisplay(config.educationExperiences(currentUser));

    // `unofficial_transcripts_description_part_two` links the registrar's address. It used to be
    // hardcoded inside the locale string; MailtoLink renders the address as its own text, so the
    // string now carries a self-closing <mail/> slot and the address is derived from the program
    // config (same derivation as EnrollmentBox).
    // Read the program state that put this section on screen, not the user's actively selected one:
    // a learner with program states in two brands would otherwise be shown the other brand's
    // registrar. Falls back to the default AOI when no state matches.
    const userProgramState = transcriptSectionUserProgramState(currentUser);
    const aoiFilters = userProgramState ? { userProgramState } : {};
    const programType = getAoiRecord(currentUser, null, aoiFilters)?.programType;
    const supportEmailDomain = programType ? getProgramConfigValue<string>(programType, 'supportEmailDomain') : null;
    const registrarEmail = supportEmailDomain ? `registrar@${supportEmailDomain}` : null;

    // The `mail` slot is always supplied. <Trans> turns a slot the components map doesn't define
    // back into literal text, so omitting it would print `<mail/>` to the learner.
    const faqComponents: FaqComponents = path => ({
        faq: <InlineLink className="text-xs" onPress={() => loadFaqUrl(path, '_blank')} />,
        mail: registrarEmail ? <MailtoLink className="text-xs" email={registrarEmail} /> : <span />,
    });

    const subText = (subTextKey: string) => (
        <Trans t={t} i18nKey={subTextKey} components={faqComponents(FAQ_PATHS[subTextKey] ?? FALLBACK_FAQ_PATH)} />
    );

    return (
        <div className="flex flex-col gap-4 py-6">
            <Text as="h2" color="black" weight="semibold" className="text-base">
                {t(config.headerKey)}
            </Text>

            {config.subTextKeys.map(subTextKey => (
                <Text as="p" variant="small" key={subTextKey}>
                    {subText(subTextKey)}
                </Text>
            ))}

            {config.showBody
                ? experiences.map(experience => (
                      <EducationExperienceRow
                          key={experience.id}
                          config={config}
                          experience={experience}
                          cannotUploadOnThisDevice={cannotUploadOnThisDevice}
                          subText={subText}
                      />
                  ))
                : null}

            {config.showFooter ? (
                <Text as="p" variant="small">
                    {subText('settings.edit_documents.unofficial_transcripts_final_note')}
                </Text>
            ) : null}
        </div>
    );
}

function EducationExperienceRow({
    config,
    experience,
    cannotUploadOnThisDevice,
    subText,
}: {
    config: TranscriptViewConfig;
    experience: CamelCasedEducationExperience;
    cannotUploadOnThisDevice: boolean;
    subText: (subTextKey: string) => ReactElement;
}) {
    const { t } = useTranslation('back_royal');
    const $injector = useAngularContext();
    const { upload, uploading, errorMessage } = useUploadDocument();
    const syncTranscriptToCurrentUser = useSyncTranscriptToCurrentUser(experience.id);

    // Uploads happen outside any save button, so the list is local state seeded from the user.
    const [transcripts, setTranscripts] = useState<CamelCasedTranscript[]>(() => experience.transcripts);

    // Everything below reads the merged experience rather than the prop, so a just-uploaded
    // transcript immediately shows up in the list and clears the "please upload" prompt without
    // needing a reload.
    const mergedExperience = { ...experience, transcripts };
    const subTextKey = config.educationExperienceSubTextKey(mergedExperience);
    const uploaded = unofficialTranscripts(mergedExperience);

    const title = [getDegreeName(experience.degree, t), experience.major].filter(Boolean).join(' ');
    const showList = config.showUnofficialTranscriptList(mergedExperience) && uploaded.length > 0;

    const viewTranscript = (document: { id: string; fileContentType: string }) =>
        $injector?.get<PrivateUserDocumentsHelper>('PrivateUserDocumentsHelper')?.viewDocument({
            downloadUrl: transcriptDownloadUrl(document.id),
            fileContentType: document.fileContentType,
        });

    const onFileSelected = async (file: File) => {
        const result = await upload<CamelCasedTranscript>({
            url: UPLOAD_URL,
            file,
            recordFields: { education_experience_id: experience.id, transcript_type: 'unofficial' },
            responseKey: 's3_transcript_assets',
        });
        if (!result) return;

        setTranscripts(previous => [...previous, result]);
        syncTranscriptToCurrentUser(result);
    };

    return (
        <div className="flex flex-col gap-2">
            <Text as="h3" color="black" weight="semibold" className="text-sm">
                {title ? `${title} - ` : ''}
                {orgName(experience)}
                {experience.graduationYear ? ` (${experience.graduationYear})` : ''}
            </Text>

            {subTextKey ? (
                <Text as="p" variant="small">
                    {subText(subTextKey)}
                </Text>
            ) : null}

            {showList ? (
                <Text as="p" variant="small">
                    {t('settings.edit_documents.unofficial_transcripts_upload_thank_you')}
                </Text>
            ) : null}

            {/* The list goes inside the upload button so it sits beside it, as in the English
                proficiency section. Where there's no upload button — an accepted learner's read-only
                view — it stands on its own instead. */}
            {config.showUploadButton(mergedExperience) ? (
                <DocumentUploadButton
                    accept={DOCUMENT_UPLOAD_ACCEPT}
                    isDisabled={uploading || cannotUploadOnThisDevice}
                    uploading={uploading}
                    errorMessage={errorMessage}
                    onFileSelected={file => void onFileSelected(file)}
                >
                    {showList ? <UploadedDocumentList documents={uploaded} onView={viewTranscript} /> : null}
                </DocumentUploadButton>
            ) : (
                showList && <UploadedDocumentList documents={uploaded} onView={viewTranscript} />
            )}
        </div>
    );
}

// Mirrors a newly-uploaded transcript onto the shared currentUser. The upload endpoint renders the
// record rather than push messages, so nothing syncs this for us, and the Angular side still reads
// education experiences off $rootScope.currentUser.
function useSyncTranscriptToCurrentUser(educationExperienceId: string) {
    const $rootScope = useRootScope();

    return (transcript: CamelCasedTranscript) => {
        // The Iguana career profile type doesn't describe its embedded education experiences.
        const careerProfile = $rootScope.currentUser?.career_profile as unknown as
            | { education_experiences?: { id: string; transcripts?: unknown[] }[] }
            | undefined;
        const experience = careerProfile?.education_experiences?.find(
            candidate => candidate.id === educationExperienceId,
        );
        if (!experience) return;

        // Snake_cased on the way in: EducationExperience#unofficialTranscripts filters on
        // `transcript_type`, so a camelCased record would be invisible to Angular.
        experience.transcripts = [...(experience.transcripts ?? []), transformKeyCase(transcript, { to: 'snakeCase' })];
        $rootScope.$applyAsync();
    };
}
